import type { SalaryModel, SalaryModelVersionSummary } from '@brush/api-client';
import { BuilderSection, Labelled, Pill, PlatformBadge, controlClass } from '@brush/ui';
import { useMemo } from 'react';

/**
 * Section 3 — how this contest's players get their prices.
 *
 * <b>Two answers, and "no model" is a first-class one.</b> §4.5's cold start gives an operator two
 * supported paths and asks them to pick explicitly: derive from a salary model, or curate and price
 * by hand. A room whose first series has only just finished has nothing to derive from, and a
 * screen that made a model compulsory would leave them unable to run a contest at all.
 *
 * <b>And this section is not always here.</b> An auction contest discovers its prices by bidding
 * and arrives with no model and nothing to review — so the section asks whether the *format* prices
 * up front (`formatNeedsSalaries`) rather than assuming every contest does. That is the assumption
 * §3's extension table exists to keep out of the code, and it is why `SalaryModelVersionId` is
 * nullable in the first place.
 *
 * Only <b>published</b> versions are offered. A draft is a document somebody is still editing, and
 * pricing a contest from one would freeze prices derived from a model that then changed underneath
 * them — which is the exact guarantee §4.1's version pinning exists to make.
 */
export function ContestSalarySection({
  step,
  models,
  modelsLoading,
  choice,
  onChoiceChange,
  pinnedVersionId,
  readOnly,
  mismatch,
}: {
  step: number;
  models: readonly SalaryModel[];
  modelsLoading: boolean;
  /**
   * The version this draft has chosen, or null for §4.5's manual pool.
   *
   * A version *id* rather than a model plus a number, because that is what the contest stores and
   * what the PATCH takes. The picker resolves it back through the catalogue.
   */
  choice: string | null;
  onChoiceChange: (next: string | null) => void;
  /**
   * What the contest has actually pinned, once it has published. Non-null makes this section a
   * statement rather than a question — §4.1's whole guarantee is that this cannot move.
   */
  pinnedVersionId: string | null;
  readOnly: boolean;
  /**
   * The pool below was priced by something other than what is chosen here, or by a model this
   * screen has lost track of. A warning rather than a blocker — see `salaryModelMismatch`.
   */
  mismatch: string | null;
}) {
  // A model with no published version cannot price anything, so it is dropped from the picker
  // rather than offered and refused. Its own screen is where somebody publishes it.
  const publishable = useMemo(
    () =>
      models
        .filter((m) => !m.isArchived && m.versions.some(isPublished))
        .map((m) => ({ model: m, versions: m.versions.filter(isPublished).sort(byNewest) })),
    [models],
  );

  if (pinnedVersionId !== null) {
    return (
      <BuilderSection step={step} title="Salary model" summary="Pinned when this contest published.">
        <PinnedModel models={models} pinnedVersionId={pinnedVersionId} />
      </BuilderSection>
    );
  }

  const selected =
    publishable.find((p) => p.versions.some((v) => v.id === choice)) ?? null;
  const selectedVersion = selected?.versions.find((v) => v.id === choice) ?? null;

  return (
    <BuilderSection
      step={step}
      title="Salary model"
      summary={
        choice === null
          ? 'Not set — you will price the pool by hand.'
          : (selected?.model.name ?? 'A model this room can no longer see.')
      }
    >
      <div className="space-y-3">
        <Labelled
          label="Price the pool with"
          hint="Only published versions appear. A contest keeps the exact version it publishes with, so editing the model later never moves a price anybody has drafted against."
        >
          <select
            value={selected?.model.id ?? ''}
            disabled={readOnly}
            onChange={(e) => {
              const id = e.target.value;
              if (id === '') {
                onChoiceChange(null);
                return;
              }
              // Defaults to the newest published version, which is the one a room adopting the
              // model would be priced by — the same default the clone route takes.
              const model = publishable.find((p) => p.model.id === id);
              onChoiceChange(model?.versions[0]?.id ?? null);
            }}
            className={`${controlClass} w-full`}
          >
            <option value="">Price by hand — no model</option>
            {publishable.map(({ model }) => (
              <option key={model.id} value={model.id}>
                {model.name}
                {model.isPlatform ? ' (platform)' : ''}
              </option>
            ))}
          </select>
        </Labelled>

        {selected && selected.versions.length > 1 ? (
          <Labelled
            label="Version"
            hint="Older published versions are still usable — useful for running a second contest on the same prices as the first."
          >
            <select
              value={selectedVersion?.id ?? selected.versions[0]!.id}
              disabled={readOnly}
              onChange={(e) => onChoiceChange(e.target.value)}
              className={`${controlClass} w-full`}
            >
              {selected.versions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.versionNumber}
                  {v.versionNumber === selected.versions[0]!.versionNumber ? ' — newest' : ''}
                </option>
              ))}
            </select>
          </Labelled>
        ) : null}

        {selected?.model.isPlatform ? (
          <p className="flex flex-wrap items-center gap-2 text-[11.5px] text-ink-800/60">
            <PlatformBadge />
            Built by the platform. You can price with it; changing it is not yours to do — clone it
            from Settings → Salary Models if you want your own.
          </p>
        ) : null}

        {mismatch ? (
          <p className="border border-line border-l-4 border-l-warn bg-surface p-3 text-[11.5px] leading-relaxed text-ink-900">
            {mismatch}
          </p>
        ) : null}

        {choice === null ? (
          <p className="border border-line border-l-4 border-l-warn bg-surface p-3 text-[11.5px] leading-relaxed text-ink-900">
            {/* §4.5's manual path, and the honest version of what it costs. Offered rather than
                blocked — a room's first contest has no history to price from, and refusing it
                outright is the "block" default that spec deliberately makes optional. */}
            <strong className="text-ink-950">You will set every price yourself.</strong> Section 4
            starts empty: add the players you want draftable and give each one a price and a reason.
            Nothing works out who is worth what, so this is real work for a pool of any size — but
            it is the only way to run a contest before you have a finished series to price from.
          </p>
        ) : null}

        {!modelsLoading && publishable.length === 0 ? (
          <p className="text-[11.5px] leading-relaxed text-ink-800/60">
            This room has no published salary model, so there is nothing to price with yet. Build
            one in <strong className="text-ink-950">Settings → Salary Models</strong>, or price this
            pool by hand.
          </p>
        ) : null}
      </div>
    </BuilderSection>
  );
}

/**
 * What a published contest priced with — a fact, not a control.
 *
 * §9.9's rule for a pinned points system, applied to the same problem: before publishing there is
 * a choice to make, afterwards there is a fact, and the useful thing is *why* it cannot move. A
 * greyed-out picker says "not now" where the truth is "never".
 */
function PinnedModel({
  models,
  pinnedVersionId,
}: {
  models: readonly SalaryModel[];
  pinnedVersionId: string;
}) {
  const found = models
    .flatMap((model) => model.versions.map((version) => ({ model, version })))
    .find((pair) => pair.version.id === pinnedVersionId);

  return (
    <div className="space-y-2">
      {found ? (
        <p className="flex flex-wrap items-center gap-2 text-sm text-ink-950">
          {found.model.isPlatform ? <PlatformBadge /> : null}
          <span className="font-medium">{found.model.name}</span>
          <Pill>v{found.version.versionNumber}</Pill>
        </p>
      ) : (
        // A pinned version this room cannot see: an archived model, or a platform one withdrawn
        // from the catalogue. The contest is unaffected — it holds the version, not a reference to
        // a list — so this states that rather than reading as a fault.
        <p className="text-sm text-ink-800/70">
          A salary model version this room’s catalogue no longer lists. The prices below are still
          exactly what it produced.
        </p>
      )}
      <p className="text-[11.5px] leading-relaxed text-ink-800/60">
        Editing that model — or publishing a new version of it — changes nothing here. This contest
        keeps the version it published with, which is what makes the prices below defensible a year
        from now.
      </p>
    </div>
  );
}

/** A version priced against is a published one; nothing else may be pinned. */
function isPublished(version: SalaryModelVersionSummary): boolean {
  return version.status === 'published';
}

function byNewest(a: SalaryModelVersionSummary, b: SalaryModelVersionSummary): number {
  return b.versionNumber - a.versionNumber;
}
